import { API_BASE_URL } from "../config";
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { MarketContext } from "../context/MarketContext";

const Positions = () => {
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exitingId, setExitingId] = useState(null);

  const { refreshWallet } = useContext(AuthContext);
  const { getLtp, addSubscribedSymbols } = useContext(MarketContext);

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/client/allPositions`);
      const positionsData = Array.isArray(res.data) ? res.data : (res.data?.positions || []);
      setAllPositions(positionsData);

      // Subscribe symbols to live market feed
      const symbolsToSubscribe = Array.isArray(positionsData) ? positionsData.map((p) => p.name) : [];
      addSubscribedSymbols(symbolsToSubscribe);
    } catch (err) {
      console.error("Error fetching positions:", err);
      setAllPositions([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 5000);
    return () => clearInterval(interval);
  }, [addSubscribedSymbols]);

  const handleSquareOff = async (position) => {
    const ltp = getLtp(position.name, position.price || position.avg);
    if (!window.confirm(`Are you sure you want to square off ${position.qty} qty of ${position.name} at ₹${ltp}?`)) return;
    setExitingId(position._id || position.id);

    try {
      await axios.post(`${API_BASE_URL}/api/client/exitPosition`, {
        positionId: position._id || position.id,
        name: position.name,
        exitPrice: ltp
      });

      await refreshWallet();
      await fetchPositions();
    } catch (err) {
      alert("Failed to exit position: " + (err.response?.data?.message || err.message));
    } finally {
      setExitingId(null);
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <h3 className="title" style={{ margin: 0 }}>Live Open Positions ({allPositions.length})</h3>
          <p style={{ margin: "2px 0 0", color: "#666", fontSize: "12px" }}>
            Real-time P&L derived from live Angel One LTP tick stream
          </p>
        </div>
        <button onClick={fetchPositions} style={{ padding: "4px 10px", borderRadius: "4px", border: "1px solid #ccc", backgroundColor: "#fff", cursor: "pointer", fontSize: "12px" }}>
          🔄 Refresh Snapshot
        </button>
      </div>

      {loading ? (
        <p>Loading open positions...</p>
      ) : allPositions.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #eee" }}>
          <p style={{ color: "#666", fontSize: "15px", fontWeight: 500, margin: 0 }}>You do not have any open positions.</p>
          <p style={{ fontSize: "13px", color: "#999", marginTop: "6px" }}>
            Orders executed via Watchlist, Option Chain, or Strategy Builder will appear here in real time.
          </p>
        </div>
      ) : (
        <div className="order-table" style={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #eee" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5", color: "#555", fontSize: "12px" }}>
                <th style={{ padding: "10px" }}>Product</th>
                <th style={{ padding: "10px" }}>Instrument</th>
                <th style={{ padding: "10px" }}>Qty.</th>
                <th style={{ padding: "10px" }}>Avg. Price</th>
                <th style={{ padding: "10px" }}>Live LTP (₹)</th>
                <th style={{ padding: "10px" }}>Unrealized P&L</th>
                <th style={{ padding: "10px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {allPositions.map((stock, index) => {
                // Reactive live LTP from MarketContext
                const liveLtp = getLtp(stock.name, stock.price || stock.avg);
                const currValue = liveLtp * stock.qty;
                const pnl = currValue - (stock.avg * stock.qty);
                const isProfit = pnl >= 0;

                return (
                  <tr key={stock._id || index} style={{ borderBottom: "1px solid #eee", textAlign: "center", fontSize: "13px" }}>
                    <td style={{ padding: "10px" }}>
                      <span style={{ padding: "2px 6px", borderRadius: "4px", backgroundColor: "#eef6ff", color: "#1976d2", fontWeight: "bold", fontSize: "11px" }}>
                        {stock.product || "MIS"}
                      </span>
                    </td>
                    <td style={{ padding: "10px" }}><strong>{stock.name}</strong></td>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{stock.qty}</td>
                    <td style={{ padding: "10px" }}>₹{stock.avg ? stock.avg.toFixed(2) : "0.00"}</td>
                    <td style={{ padding: "10px", fontWeight: "bold", color: "#1976d2" }}>
                      ₹{liveLtp ? liveLtp.toFixed(2) : "0.00"}
                    </td>
                    <td style={{ padding: "10px", fontWeight: "bold", color: isProfit ? "#2e7d32" : "#c62828" }}>
                      ₹{pnl.toFixed(2)} ({isProfit ? "+" : ""}{((pnl / (stock.avg * stock.qty)) * 100).toFixed(2)}%)
                    </td>
                    <td style={{ padding: "10px" }}>
                      <button
                        onClick={() => handleSquareOff(stock)}
                        disabled={exitingId === stock._id}
                        style={{
                          padding: "4px 10px",
                          backgroundColor: "#ef5350",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "11px"
                        }}
                      >
                        {exitingId === stock._id ? "Exiting..." : "Exit Position"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Positions;
